import { useState } from 'react';
import type {
  PortalChannelBlock,
  PortalCrmBlock,
  PortalCrmSources,
  PortalReportLabels,
} from '../../../../../types/portalReport';
import { deliveryHistoryWithheld } from '../../../../../lib/portalReport/reportCopy';
import {
  CRM_METRIC_KEYS,
  crmSeriesValues,
  spendByBucket,
  type CrmMetricKey,
} from '../../../../../lib/portalReport/crm';
import { hasEnoughHistory } from '../../../../../lib/portalReport/delivery';
import { type ChartGrain } from '../../../../../lib/portalReport/chartPlan';
import { SectionHeading, SwitchGroup } from './controls';
import { METRIC_LABEL, ReportChartCard } from './ReportChartCard';
import { numberFull } from './format';

// CLIENT PORTAL RESTYLE — the club's own leads and sales, from the client's CRM.
//
// ⚠️⚠️ EVERY FIGURE HERE IS CLUB-LEVEL AND A READER WILL ASSUME IT IS AN
// ADVERTISING FIGURE. That is the whole risk of this panel, and it is not a
// presentation preference: a GymSales sale row carries no source, no
// contact_type and no person_id (measured, 188,726 of 188,726 blank in all
// three), so there is no version of these numbers that describes the ads alone.
//
// The misreading runs in BOTH directions, which is why both caveats are here and
// neither is optional:
//   · CONVERSION RATE looks BAD read as an ad rate — it is sales over every
//     walk-in, phone call and referral the club recorded.
//   · COST PER SALE looks GOOD read as an ad figure — it is all media over all
//     sales, so it credits the advertising with sales it had nothing to do with.
//
// ⚠️ THE CAVEATS LEAD THE FIGURES, not follow them. A caveat read after a number
// has been believed is not a caveat — the same rule the audience panel's
// stored-window note follows.
//
// ⚠️⚠️ THE FIGURE CARDS LEFT THIS FILE on 2026-09-02 and are now in
// `CrmHeroBand.tsx`, merged into the hero. They duplicated it outright: the hero
// showed 67 / 47 / 70.1% / $32.80 / $34.20 and this panel then showed 67 / 49 /
// 47 / 70.1% / $34.20 again. What stayed here is what the hero does NOT carry —
// the nesting bar, the lead-source breakdown and the chart band.
//
// ⚠️ NO CHART. There is deliberately no year-on-year here: the report's
// year-on-year contract already handles a short history correctly (`previous: []`
// plus a stated reason), and adding a CRM-shaped variant of it would be the
// special case that contract exists to avoid.

// ---------------------------------------------------------------------------
// ⚠️⚠️ THE NESTING BAR — what stops a reader subtracting
// ---------------------------------------------------------------------------

/**
 * One bar: total enquiries, with the internet-lead portion filled.
 *
 * ⚠️⚠️ EXACTLY TWO SEGMENTS, AND NEVER A THIRD FOR "ADS". The ad-attributed
 * share is the figure this report cannot compute — different attribution
 * windows, different definitions, and a sale row with no source at all — so a
 * third segment would be an invented number in the most prominent slot on the
 * page.
 *
 * ⚠️ NOT A PIE. Scott's old report used a two-slice pie at 95.4 / 4.6, which is
 * a near-complete circle with a sliver: the least readable shape available, in
 * the place a reader looks first. A bar reads at any ratio.
 *
 * ⚠️ COLOUR: kelly for the filled portion and a neutral for the rest. NOT
 * `--rpt-chan-b` (marine means GOOGLE everywhere else on this report and this
 * bar has nothing to do with channel), and NOT volt (one volt element per page,
 * and it is spent on the hero figure).
 *
 * ⚠️ ZERO ENQUIRIES OMITS THE BAR. A zero-width fill in a full-width track reads
 * as a rendering fault rather than as a fact.
 *
 * ⚠️⚠️ ITS EXPLANATORY SENTENCE IS GONE (2026-09-02), and what replaced it is
 * the merged band's ADJACENT PAIR — total leads beside internet leads, at the
 * top of the page. The nesting is now carried by the layout, which is stronger
 * than a sentence. ⚠️ The sentence itself, and the condition that would bring it
 * back, are recorded at the pair's own render site in `CrmHeroBand.tsx`.
 *
 * ⚠️ It now sits on the always-dark hero band rather than on petrol, so the
 * track takes a fixed dark-scale neutral — `var(--rpt-baseline)` drops to
 * `#5f6e62` in light mode, which on `ink-deep` is all but invisible.
 */
export function NestingBar({ leads, digitalLeads }: { leads: number; digitalLeads: number }) {
  if (leads <= 0) return null;
  // ⚠️ Clamped, because a CRM whose internet count exceeds its total would
  // otherwise draw a fill wider than its track — visibly broken, where a clamp
  // is merely wrong in a way the two printed counts still expose.
  const share = Math.max(0, Math.min(100, (digitalLeads / leads) * 100));
  const rest = leads - digitalLeads;
  return (
    <div className="rpt-nest">
      <div className="rpt-nest-track">
        <div className="rpt-nest-fill" style={{ width: `${share}%` }} />
      </div>
      <div className="rpt-nest-keys rpt-mono">
        <span>
          <span className="rpt-swatch" style={{ background: 'var(--rpt-kelly)' }} />
          {numberFull(digitalLeads)} internet leads · {share.toFixed(1)}%
        </span>
        <span>
          {/* ⚠️ The SAME token the track uses, so the key and the bar cannot
              disagree about what "everywhere else" looks like. It was a fixed
              literal for a day, because the token resolved to the LIGHT muted on
              this always-dark band; the dark-band token block in index.css fixes
              that at the source. */}
          <span className="rpt-swatch" style={{ background: 'var(--rpt-baseline)' }} />
          {numberFull(rest)} from everywhere else · {(100 - share).toFixed(1)}%
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lead sources
// ---------------------------------------------------------------------------

/**
 * ⚠️⚠️ VALUES AS STORED. A blank source is LABELLED here and never merged with a
 * literal one — if a club records both `''` and `Unknown` they are two rows,
 * because they are two rows in the CRM and merging them is a decision nobody has
 * made.
 *
 * ⚠️ The blank label is "Not recorded" rather than "Unknown", deliberately: it
 * describes what happened (nobody tagged the enquiry) and it cannot collide with
 * a literal `Unknown` value sitting beside it.
 */
function sourceLabel(source: string): string {
  return source === '' ? 'Not recorded' : source;
}

function LeadSources({ sources }: { sources: PortalCrmSources }) {
  if (sources.rows.length === 0) return null;
  const max = Math.max(...sources.rows.map((r) => r.leads), 1);
  return (
    <>
      <SectionHeading
        title="Where the enquiries came from"
        intro="As recorded in your CRM, for the selected period."
      />
      <div className="rpt-bars">
        {sources.rows.map((r) => (
          <div key={r.source} className="rpt-bar-row">
            <span className="rpt-muted">{sourceLabel(r.source)}</span>
            <span className="rpt-bar-track">
              <span className="rpt-bar-fill" style={{ width: `${(r.leads / max) * 100}%` }} />
            </span>
            <span>{numberFull(r.leads)}</span>
          </div>
        ))}
      </div>
      {/* ⚠️ The folded tail is COUNTED rather than dropped — a reader can see
          exactly how much is not on screen, which is what makes the top-N
          honest. */}
      {sources.otherSources > 0 && (
        <p className="rpt-sub rpt-muted">
          {`And ${numberFull(sources.otherLeads)} across ${sources.otherSources} smaller sources.`}
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// The CRM chart band
// ---------------------------------------------------------------------------

/**
 * Six chart cards over the CRM's own figures.
 *
 * ⚠️⚠️ THE GRAIN IS BAND-LEVEL AND THE FORM IS PER CARD — the same split the
 * Delivery band makes, for the same two reasons. See that band's own note.
 */
export function CrmCharts({
  crm,
  combined,
  labels,
  periodLabel,
}: {
  crm: PortalCrmBlock;
  /** ⚠️ The ad block the two cost metrics divide by. See the `crm` chart source. */
  combined: PortalChannelBlock;
  labels: PortalReportLabels;
  periodLabel: string;
}) {
  const [grain, setGrain] = useState<ChartGrain>('months');

  const series = grain === 'period' ? crm.periodSeries : crm.monthlySeries;
  // ⚠️⚠️ `thirteenMonthSeries`, NOT `yearOnYear.current`. The CRM chart window is
  // THIRTEEN months (the current one plus the twelve before it) and the cost
  // charts join ad spend to it BY BUCKET KEY — so a 12-month ad series would
  // leave the oldest CRM bucket with no partner, its cost would resolve to null,
  // and the chart would shade a NOT MEASURED band over a month whose spend we
  // have. Both sides move together or neither does; see `crmMonthlyRange`.
  const adSeries = grain === 'period' ? combined.periodSeries : combined.thirteenMonthSeries;
  const spend = spendByBucket(adSeries);

  return (
    <>
      <SectionHeading
        title="Your CRM over time"
        aside={
          <div className="rpt-control-group">
            <SwitchGroup
              ariaLabel="Chart period"
              value={grain}
              onChange={setGrain}
              options={[
                { value: 'months' as const, label: '12 months' },
                { value: 'period' as const, label: 'Reporting period' },
              ]}
            />
          </div>
        }
      />
      <div className="rpt-diag-cards">
        {/* ⚠️ Staff generated exists only for ClubWise; for GymSales it would be an
            always-empty card, so it is not offered at all. */}
        {CRM_METRIC_KEYS.filter((key) => key !== 'crmStaffLeads' || crm.clubwise).map((key) => (
          <CrmChartCard
            key={key}
            metric={key}
            crm={crm}
            combined={combined}
            labels={labels}
            periodLabel={periodLabel}
            grain={grain}
            values={crmSeriesValues(series, key, spend)}
          />
        ))}
      </div>
    </>
  );
}

function CrmChartCard({
  metric,
  crm,
  combined,
  labels,
  periodLabel,
  grain,
  values,
}: {
  metric: CrmMetricKey;
  crm: PortalCrmBlock;
  combined: PortalChannelBlock;
  labels: PortalReportLabels;
  periodLabel: string;
  grain: ChartGrain;
  values: Array<number | null>;
}) {
  const measured = values.filter((v) => v !== null).length;
  const currency = crm.costPerSale[0]?.currency ?? 'AUD';
  // ⚠️ THE CARD'S OWN LABEL SOURCE, not a second copy. `ReportChartCard`
  // dedupes the title against the metric name in its accessible label, so two
  // lists would announce the metric twice the moment they drifted.
  const title = METRIC_LABEL(metric, labels);
  return (
    <ReportChartCard
      title={title}
      source={{
        kind: 'crm',
        block: crm,
        adPeriod: combined.periodSeries,
        // ⚠️ THIRTEEN, matching the CRM window — see `CrmCharts`.
        adMonthly: combined.thirteenMonthSeries,
      }}
      metrics={[metric]}
      labels={labels}
      currency={currency}
      periodLabel={periodLabel}
      grain={grain}
      /* ⚠️ NO `form` — the card owns it, so its default is `DEFAULT_FORM`'s
         per-metric rule (a count opens as a bar, a ratio as a line) and its
         switch shows what this chart is actually doing. */
      /* ⚠️ THE CARD NEEDS A VISIBLE HEADING. `ReportChartCard`'s `title` is only
         its ACCESSIBLE name — the four original instances sit under a
         `SectionHeading` and the Delivery cards carry a figure, so neither
         needed one. Six untitled charts in a row are unreadable, and the
         omission is invisible to a source-shape test.

         ⚠️ THE LABEL ALONE, not a figure. Four of these six are already the
         hero's figures, and repeating them here would put the same number twice
         on one screen — which reads as two measurements that happen to agree
         rather than as one. */
      headline={<span className="rpt-mono rpt-diag-headline">{title}</span>}
      // ⚠️ Below three measured buckets there is a REASON where the chart would
      // be, not a blank space — the rule the Delivery band established, and it
      // applies harder here because two of the six can be null for a reason
      // (unmeasured ad spend) that has nothing to do with the CRM.
      chartWithheld={hasEnoughHistory(values) ? null : deliveryHistoryWithheld(measured, grain)}
    />
  );
}

export { LeadSources };
